import { createSelector } from 'reselect';
import { orderBy } from 'lodash';
import moment from 'moment';
import { StoreState } from '../../store-interfaces';
import {
  ConnectionKind,
  ConnectionModel,
  FormattedConnectionModel,
} from '../../model/ConnectionModel';
import { MailchimpConnections } from '../../model/MailchimpModel';

export const getMailchimpConnections = (state: StoreState) =>
  state.mailchimpConnections.entities;
export const getWidgetKindMailchimpConnections = (state: StoreState) =>
  state.mailchimpConnections.entities?.filter(
    connection => connection.kind === ConnectionKind.WIDGET,
  );
const getConnectionsSortBy = (state: StoreState) =>
  state.mailchimpConnectionsTableState.connectionsOrderBy;

const getMailchimpOrderedConnections = (
  connections: ConnectionModel[],
  connectionsOrderBy,
): FormattedConnectionModel[] => {
  const { orderDir, keyName } = connectionsOrderBy;
  const mappedConnections = connections.map<FormattedConnectionModel>(
    connection => {
      const { createdAt, updatedAt, active } = connection;
      const formattedCreatedAt = moment(createdAt).format('MM/DD/YYYY');
      const formattedUpdatedAt = moment(updatedAt).format('MM/DD/YYYY');
      const statusText = active ? 'Active' : 'Draft';

      return {
        ...connection,
        statusText,
        formattedUpdatedAt,
        formattedCreatedAt,
      };
    },
  );
  return orderBy(
    mappedConnections,
    [connection => connection[keyName]?.toLowerCase()],
    [orderDir],
  );
};

export const orderedMailchimpConnectionsSelector = createSelector(
  [getMailchimpConnections, getConnectionsSortBy],
  getMailchimpOrderedConnections,
);

export const selectConnectionsMailchimpAccounts = createSelector(
  [getMailchimpConnections],
  connections => {
    const accounts = connections.reduce(
      (accounts: { id: number; email: string }[], connection) => {
        if (
          accounts.find(
            account => account.id === connection.mailchimpAccessTokenId,
          )
        ) {
          return accounts;
        }
        return [
          ...accounts,
          {
            id: connection.mailchimpAccessTokenId,
            email: connection.mailchimpAccountEmail,
          },
        ];
      },
      [],
    );
    return accounts;
  },
);

export const selectMailchimpConnections = createSelector(
  [orderedMailchimpConnectionsSelector, selectConnectionsMailchimpAccounts],
  (connections, accounts): MailchimpConnections[] => {
    return accounts.map(account => {
      return {
        accountId: account.id,
        accountEmail: account.email,
        connections:
          connections.filter(
            connection => connection.mailchimpAccessTokenId === account.id,
          ) ?? [],
      };
    });
  },
);
